
import React, { useMemo } from 'react';
import DonutCard from './DonutCard';
import { Box, Card, Text, Grid, useColorMode } from 'theme-ui';
import { DoneProjectBreakdownBackend, DonutCardProps, DonutProps } from '../../../../../types/dashboard.types';
import { useSelector } from 'react-redux';
import DonutCardSkeleton from '../../skeleton/DonutCardSkeleton';



type BackendCard = DonutCardProps;

const DEFAULT_COLORS = {
    planCheck: '#2F7FBD',
    revision: '#41447D',
    noComments: '#BAD531'
} as const;

function toCardsFromBackend(raw?: DoneProjectBreakdownBackend | null): BackendCard[] {
    if (!raw) return [];
    return Object.entries(raw as Record<string, any>).reduce<BackendCard[]>((acc, [key, val]) => {
        if (!val || typeof val !== 'object') return acc;
        const hasMetrics = typeof val.total === 'number' || typeof val.planCheck === 'number' || typeof val.revision === 'number' || typeof val.noComments === 'number';
        if (!hasMetrics) return acc;

        const title = String(key).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
        const segs: BackendCard['segments'] = [
            { key: 'plan_check', label: 'Plan Check', value: val.planCheck ?? val.plan_check ?? 0, color: DEFAULT_COLORS.planCheck },
            { key: 'revision', label: 'Revision', value: val.revision ?? 0, color: DEFAULT_COLORS.revision },
            { key: 'no_comments', label: 'No comments', value: val.noComments ?? val.no_comments ?? 0, color: DEFAULT_COLORS.noComments },
        ];

        acc.push({
            id: key,
            title,
            totalDone: typeof val.total === 'number' ? val.total : segs.reduce((s, x) => s + (x.value || 0), 0),
            segments: segs,
        });
        return acc;
    }, []);
}

const DoneProjectBreakdown: React.FC<DonutProps> = ({ doneProjectBreakdown, loading }) => {
    const cards = useMemo(() => toCardsFromBackend(doneProjectBreakdown), [doneProjectBreakdown]);
    const { flag } = useSelector((state: any) => state.dashboardData || {});
    // No local dummy cards: render only backend-provided cards. Keep sizing stable so layout matches Figma even when
    // fewer than five cards are present.


    // Use backend cards directly; do not render dummy data per user's request.
    const displayCards = cards;
    const [colorMode] = useColorMode();
    const bgGeographic = colorMode === 'light' ? "#ffffff" : "#2e354db8";
    const bgDashboard = colorMode === 'light' ? "#ffffff" : "#252b36";
    const titleHeader = colorMode === 'light' ? "#453521" : "#f8f9fa";
    const isDashboardLoading = loading && [
        "all-data",
        "dashboard-data",
        "dashboard-time-data",
        "dashboard-project-type-data"
    ].includes(flag);
    return (
        <div className="row">
            <div className="col-12">
                <Card sx={{ padding: 0, borderRadius: '20px', boxShadow: '0 8px 24px rgba(36,28,25,0.06)', background: 'none' }}>
                    <div className='py-3 px-3' style={{ backgroundColor: bgGeographic, borderRadius: '20px' }}>
                        <Box className='mx-3'  >
                            <Text variant='Poppins16Bold16lh' sx={{ color: titleHeader, fontSize: ["10px", "12px", "16px", "18px"], fontWeight: 700, mb: "20px" }}>Project Breakdown (PC & REV) </Text>
                        </Box>

                        {/* Theme UI Grid replacing Bootstrap columns for precise responsive control */}
                        {/* Render first three cards in a 3-column grid, then center the remaining two below */}
                        {(() => {

                            const DONUT_SIZE = 260; // px, controls visual size to match Figma

                            const renderCard = (card?: BackendCard, idx?: number) => {
                                const totalFromSegments =
                                    card?.segments?.reduce((s: number, seg: { value: number }) => s + (seg.value || 0), 0) || 0;

                                const total =
                                    typeof card?.totalDone === "number" ? card.totalDone : totalFromSegments;

                                return (
                                    <Box sx={{ display: "flex", width: "100%" }} key={card?.id || idx}>
                                        <Card
                                            variant="donutChartCard"
                                            sx={{
                                                width: "100%",
                                                minHeight: isDashboardLoading ? "418px" : "410px",
                                                display: "flex",
                                                flexDirection: "column",
                                                marginTop: '15px',
                                                justifyContent: "space-between",
                                                px: isDashboardLoading ? "0px" : [2, 2, 3],
                                                py: isDashboardLoading ? "0px" : [2, 3],
                                                backgroundColor: bgDashboard,
                                            }}
                                        >
                                            {/* ✅ TITLE (Skeleton or Real) */}
                                            <Box sx={{ pt: 2, minHeight: "28px" }}>
                                                {!isDashboardLoading && (
                                                    <Text
                                                        variant="Poppins16Bold16lh"
                                                        sx={{
                                                            color: colorMode === 'light' ? '#453521' : '#f8f9fab5',
                                                            fontSize: ["12px", "14px", "14px", "16px"],
                                                            fontWeight: 600,
                                                        }}
                                                    >
                                                        {card?.title}
                                                    </Text>
                                                )}
                                            </Box>

                                            {/* ✅ DONUT / SKELETON (SAME HEIGHT ALWAYS) */}
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    justifyContent: "center",
                                                    alignItems: "center",
                                                    height: `${DONUT_SIZE}px`,
                                                }}
                                            >
                                                <DonutCard
                                                    id={card?.id}
                                                    title={card?.title}
                                                    totalDone={total || 0}
                                                    segments={card?.segments}
                                                    loading={loading}
                                                    size={DONUT_SIZE}
                                                    colorMode={colorMode}
                                                />
                                            </Box>

                                            {/* ✅ RESERVED SPACE (NO SHIFT) */}
                                            <Box sx={{ pb: 3 }} />
                                        </Card>
                                    </Box>
                                );
                            };


                            return (
                                <Box sx={{ gap: '40px' }}>
                                    <Grid columns={[1, 1, 2, 4]}>
                                        {cards.map((c, idx) =>
                                            renderCard(c as BackendCard, idx)
                                        )}
                                        {(loading && cards.length === 0 && flag !== "dashboard-trades-type-worker-manager") && (
                                            Array.from({ length: 4 }).map((_, index) => (
                                                <DonutCardSkeleton key={index} />
                                            ))
                                        )}
                                    </Grid>
                                </Box>
                            );
                        })()}
                    </div>

                </Card>
            </div>
        </div>
    );
};

export default React.memo(DoneProjectBreakdown);